import { useMemo } from 'react';
import {
  Typography,
  TypographyVariants,
  TypographyWeights,
  TypographyColors,
} from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { FieldSection } from '@/components/common';
import styles from './ShipmentOverviewTabView.module.scss';

/**
 * Props:
 *   shipment  the shipment detail view model
 */
export const ShipmentOverviewTabView = ({ shipment }) => {
  const t = useTranslations();

  const declarationSectionFields = useMemo(
    () => [
      {
        label: t('FIELD_SHIPMENT_QUANTITY'),
        value: shipment.declarationQuantities.shipmentQuantity,
      },
      {
        label: t('FIELD_RECEIVED_DECLARATION_QUANTITY'),
        value: shipment.declarationQuantities.receivedDeclarationQuantity,
      },
      {
        label: t('FIELD_PENDING_QUANTITY'),
        value: shipment.declarationQuantities.pendingQuantity,
      },
      {
        label: t('FIELD_AVAILABLE_QUANTITY'),
        value: shipment.declarationQuantities.availableQuantity,
      },
    ],
    [t, shipment.declarationQuantities],
  );

  const workingSectionFields = useMemo(
    () => [
      {
        label: t('FIELD_SHIPMENT_QUANTITY'),
        value: shipment.workingQuantities.shipmentQuantity,
      },
      {
        label: t('FIELD_RECEIVED_DECLARATION_QUANTITY'),
        value: shipment.workingQuantities.receivedDeclarationQuantity,
      },
      {
        label: t('FIELD_PENDING_QUANTITY'),
        value: shipment.workingQuantities.pendingQuantity,
      },
      {
        label: t('FIELD_AVAILABLE_QUANTITY'),
        value: shipment.workingQuantities.availableQuantity,
      },
    ],
    [t, shipment.workingQuantities],
  );

  const trackingSectionFields = useMemo(
    () => [
      {
        label: t('FIELD_SHIPMENT_QUANTITY'),
        value: shipment.trackingQuantities.shipmentQuantity,
      },
      {
        label: t('FIELD_RECEIVED_DECLARATION_QUANTITY'),
        value: shipment.trackingQuantities.receivedDeclarationQuantity,
      },
      {
        label: t('FIELD_PENDING_QUANTITY'),
        value: shipment.trackingQuantities.pendingQuantity,
      },
      {
        label: t('FIELD_AVAILABLE_QUANTITY'),
        value: shipment.trackingQuantities.availableQuantity,
      },
    ],
    [t, shipment.trackingQuantities],
  );

  const preferenceFields = useMemo(
    () => [
      { label: t('FIELD_GHG_EMISSIONS'), value: shipment.ghgEmissions },
      {
        label: t('FIELD_GHG_EMISSION_SAVINGS'),
        value: shipment.ghgEmissionSavings,
      },
      { label: t('FIELD_RAW_MATERIAL_TYPE'), value: shipment.rawMaterialType },
      { label: t('FIELD_COUNTRY_OF_ORIGIN'), value: shipment.countryOfOrigin },
      {
        label: t('FIELD_FEEDSTOCK_CATEGORY'),
        value: shipment.feedstockCategory,
      },
      {
        label: t('FIELD_ALLOCATION_PREFERENCE'),
        value: shipment.allocationPreference,
      },
    ],
    [t, shipment],
  );

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <Typography
          variant={TypographyVariants.bodySm}
          weight={TypographyWeights.semibold}
          color={TypographyColors.primary}
        >
          {t('INCOMING_SHIPMENTS_DETAIL_CARD_TITLE')}
        </Typography>
      </div>
      <div className={styles.cardBody}>
        {shipment.declarationUnit && (
          <FieldSection
            heading={t('SECTION_QUANTITY_IN_UNIT', {
              unit: shipment.declarationUnit,
            })}
            fields={declarationSectionFields}
          />
        )}
        {shipment.workingUnit && (
          <FieldSection
            heading={t('SECTION_QUANTITY_IN_UNIT', {
              unit: shipment.workingUnit,
            })}
            fields={workingSectionFields}
          />
        )}
        {shipment.trackingUnit && (
          <FieldSection
            heading={t('SECTION_QUANTITY_IN_UNIT', {
              unit: shipment.trackingUnit,
            })}
            fields={trackingSectionFields}
          />
        )}
        <FieldSection heading={t('SECTION_PREFERENCES')} fields={preferenceFields} />
      </div>
    </div>
  );
};
